// Onboarding for the forecasting self-serve flow: the CP picks the channels +
// KPIs universe once; saving persists that scope and kicks off Data QA, which
// evaluates every channel × platform × tactic × KPI combination in scope so the
// Request page can offer only the viable paths.
import './ForecastOnboarding.scss';
import { Dispatch, ReactNode, SetStateAction, useEffect, useMemo, useState } from 'react';
import { Button } from '@Components/Button/Button';
import { Appearance, Design, Size } from '@Components/designConstants';
import { FullPageSpinner, Spinner } from '@Components/Spinner';
import { useSetError } from '../../../redux/modals';
import { awaitJSON, MetricsLambdaFetch } from '../../../utils/fetch-utils';
import { useCompanyInfo } from '../../../utils/hooks/useCompanyInfo';
import { DataQaConfig } from '../forecasting-constants';
import { useForecastChannels } from '../useForecastChannels';

// /saveDataQaConfig persists the scope, kicks off a Data QA run, and returns its id.
interface SaveDataQaConfigResponse {
  dataQaId: string;
}

interface ChipSectionProps {
  title: string;
  options: string[];
  selected: string[];
  setSelected: Dispatch<SetStateAction<string[]>>;
  toggle: (list: string[], setList: Dispatch<SetStateAction<string[]>>, item: string) => void;
}

const ChipSection = ({
  title,
  options,
  selected,
  setSelected,
  toggle
}: ChipSectionProps): ReactNode => {
  const allSelected = selected.length === options.length;
  return (
    <section className='fc-ob-card'>
      <header className='fc-ob-card-head'>
        <h3>{title}</h3>
        <span className='fc-ob-card-meta'>
          <span className='fc-ob-card-count'>
            {selected.length} of {options.length} selected
          </span>
          <Button
            appearance={Appearance.EMPTY}
            size={Size.SM}
            onClick={() => setSelected(allSelected ? [] : [...options])}
          >
            {allSelected ? 'Clear' : 'Select all'}
          </Button>
        </span>
      </header>
      <div className='fc-ob-chips'>
        {options.map(option => (
          <Button
            size={Size.LG}
            key={option}
            appearance={selected.includes(option) ? Appearance.FILLED : Appearance.OUTLINED}
            design={selected.includes(option) ? Design.BRAND : Design.NEUTRAL}
            className='fc-ob-chip'
            onClick={() => toggle(selected, setSelected, option)}
          >
            {option}
          </Button>
        ))}
      </div>
    </section>
  );
};

interface ForecastOnboardingProps {
  submitDataQaConfig: (channels: string[], kpis: string[], dataQaId?: string) => void;
  savedScope?: DataQaConfig;
}

const ForecastOnboarding = ({ submitDataQaConfig, savedScope }: ForecastOnboardingProps) => {
  const { cid: company } = useCompanyInfo();
  const setError = useSetError();

  const dataTree = useForecastChannels(company);
  const [channels, setChannels] = useState<string[]>([]);
  const [kpis, setKpis] = useState<string[]>([]);
  const [running, setRunning] = useState(false);

  useEffect(() => {
    if (savedScope?.channels && savedScope?.channels.length) {
      setChannels(savedScope.channels);
    }
    if (savedScope?.kpis && savedScope?.kpis.length) {
      setKpis(savedScope.kpis);
    }
  }, [savedScope]);

  const channelOptions = useMemo(() => Object.keys(dataTree ?? {}).sort(), [dataTree]);
  const kpiOptions = useMemo(() => {
    const all = new Set<string>();
    Object.values(dataTree ?? {}).forEach(platforms =>
      Object.values(platforms).forEach(kpiTactics =>
        Object.keys(kpiTactics).forEach(k => all.add(k))
      )
    );
    return [...all].sort();
  }, [dataTree]);

  const toggle = (list: string[], setList: Dispatch<SetStateAction<string[]>>, item: string) =>
    setList(list.includes(item) ? list.filter(x => x !== item) : [...list, item]);

  const combos = channels.length * kpis.length;
  const canRun = channels.length > 0 && kpis.length > 0;

  const saveDataQaConfig = async () => {
    if (running) return;
    setRunning(true);
    try {
      const res = await MetricsLambdaFetch('/saveDataQaConfig', {
        method: 'POST',
        body: { company, channels, kpis }
      });
      // Save succeeded; reading the run id is best-effort — swallow parse errors
      // (empty/non-JSON body) so they don't surface as a save failure.
      let dataQaId: string | undefined;
      try {
        ({ dataQaId } = await awaitJSON<SaveDataQaConfigResponse>(res));
      } catch {
        dataQaId = undefined;
      }
      submitDataQaConfig(channels, kpis, dataQaId);
    } catch (e) {
      setError({
        message: `Couldn't save the Data QA configuration: ${e?.message ?? e}`,
        reportError: e
      });
      // Reset running only on failure; the success path unmounts on navigation.
      setRunning(false);
    }
  };

  return !dataTree ? (
    <FullPageSpinner />
  ) : (
    <div className='fc-onboarding' data-testid='forecast-onboarding'>
      <p className='fc-ob-subtitle'>
        Choose the channels and KPIs to make available for forecasting — every channel × KPI
        combination in scope gets QA&rsquo;d for forecasting viability.
      </p>

      <div className='fc-ob-cards'>
        <ChipSection
          title='Channels'
          options={channelOptions}
          selected={channels}
          setSelected={setChannels}
          toggle={toggle}
        />
        <ChipSection
          title='KPIs'
          options={kpiOptions}
          selected={kpis}
          setSelected={setKpis}
          toggle={toggle}
        />
      </div>

      <div className='fc-ob-footer'>
        <span className='fc-ob-count'>
          {canRun ? (
            <>
              <strong>{combos}</strong> channel × KPI combinations in scope
            </>
          ) : (
            'Pick at least one channel and one KPI'
          )}
        </span>
        <Button
          size={Size.LG}
          disabled={!canRun}
          loading={running}
          onClick={saveDataQaConfig}
          data-testid='run-data-qa-button'
        >
          {running ? 'Saving & starting Data QA…' : 'Save & Start Data QA'}
        </Button>
      </div>

      {running && (
        <div className='fc-ob-overlay' data-testid='qa-running-overlay' role='status'>
          <Spinner />
          <p>
            Saving your scope and starting Data QA across <strong>{combos}</strong> channel × KPI
            combinations…
          </p>
        </div>
      )}
    </div>
  );
};

export { ForecastOnboarding };
